/**
 * Admin modal: place a session's confirmed players onto courts.
 *
 * Tap a player in the "Not on a court yet" pool to select them, then tap a
 * court to seat them there. Tap a seated player to pull them back off.
 * "Auto-fill" assigns everyone first-come-first-served. An incomplete court
 * reads "waiting on N more".
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import {
  assignSessionCourts,
  getSessionCourts,
  sendSessionFacilityRequest,
  setSessionCourtLabels,
  type CourtLayoutDTO,
  type RsvpPersonDTO,
} from "../api";

export function CourtAssignModal({
  sessionId,
  title,
  onClose,
}: {
  sessionId: string;
  title: string;
  onClose: () => void;
}) {
  const [layout, setLayout] = useState<CourtLayoutDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // One editable label per court slot (defaults to the slot number).
  const [labels, setLabels] = useState<string[]>([]);

  function labelsFromLayout(d: CourtLayoutDTO): string[] {
    return d.courts.map((c) => c.label);
  }

  useEffect(() => {
    let cancelled = false;
    getSessionCourts(sessionId)
      .then((d) => {
        if (cancelled) return;
        setLayout(d);
        setLabels(labelsFromLayout(d));
      })
      .catch((e) => !cancelled && setError(String(e?.message ?? e)));
    return () => { cancelled = true; };
  }, [sessionId]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function apply(
    body: { auto: true; order?: "fcfs" | "rank" } | { user_id: string; court_number: number | null },
  ) {
    setBusy(true);
    setError(null);
    try {
      const next = await assignSessionCourts(sessionId, body);
      setLayout(next);
      setLabels(labelsFromLayout(next));
      setSelected(null);
    } catch (e) {
      setError(String((e as Error)?.message ?? e));
    } finally {
      setBusy(false);
    }
  }

  function onSeatOnCourt(courtNumber: number) {
    if (!selected || busy) return;
    void apply({ user_id: selected, court_number: courtNumber });
  }

  function onRemoveFromCourt(userId: string) {
    if (busy) return;
    void apply({ user_id: userId, court_number: null });
  }

  // Persist the per-court numbers. The server normalizes (blank → slot number,
  // all-default → plain numbering); here we just skip the round-trip when the
  // boxes still match what's displayed.
  async function saveLabels(next: string[]) {
    if (!layout) return;
    if (JSON.stringify(next) === JSON.stringify(labelsFromLayout(layout))) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await setSessionCourtLabels(sessionId, next);
      setLayout(updated);
      setLabels(labelsFromLayout(updated));
    } catch (e) {
      setError(String((e as Error)?.message ?? e));
    } finally {
      setBusy(false);
    }
  }

  async function sendFacilityRequest() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await sendSessionFacilityRequest(sessionId);
      setNotice(
        res.facility_email
          ? `Court request sent to ${res.facility_email}.`
          : "Court request sent to the facility.",
      );
      // Finalizing re-seats players and waitlists any overflow — refresh so the
      // modal reflects the locked roster.
      const refreshed = await getSessionCourts(sessionId);
      setLayout(refreshed);
      setLabels(labelsFromLayout(refreshed));
    } catch (e) {
      setError(String((e as Error)?.message ?? e));
    } finally {
      setBusy(false);
    }
  }

  const perCourt = layout?.per_court ?? null;

  return createPortal(
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-ink/55 cs-modal-overlay" onClick={onClose} />
      <div className="absolute bottom-0 left-0 right-0 cs-modal-card sm:inset-0 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2 sm:max-w-lg sm:mx-auto sm:my-0">
        <div
          className="max-w-lg mx-auto px-4 pb-4 sm:pt-0"
          style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
        >
          <div className="bg-bg border-2 border-ink rounded-t-xl sm:rounded-xl shadow-2xl overflow-hidden max-h-[85vh] flex flex-col">
            <div className="px-5 py-3 bg-ink text-bg flex items-center justify-between shrink-0">
              <div className="min-w-0">
                <div className="font-mono text-[11px] uppercase tracking-[0.16em] truncate">
                  Assign courts
                </div>
                <div className="text-bg/60 text-xs truncate">{title}</div>
              </div>
              <button
                onClick={onClose}
                className="text-bg/60 hover:text-bg font-mono text-2xl leading-none ml-3"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="px-5 py-4 overflow-y-auto">
              {error && (
                <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                  {error}
                </div>
              )}
              {notice && (
                <div className="mb-3 text-sm text-green-800 bg-green-50 border border-green-200 rounded px-3 py-2">
                  {notice}
                </div>
              )}
              {layout == null && !error ? (
                <div className="text-ink/55 text-sm py-6 text-center">Loading…</div>
              ) : layout && (perCourt == null || layout.court_count == null) ? (
                <div className="text-ink/55 text-sm py-6 text-center">
                  Set this session's format and court count first, then you can
                  assign players to courts.
                </div>
              ) : layout ? (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                    <div className="font-mono text-[10px] uppercase tracking-wider text-ink/55 min-w-0">
                      {selected ? "Tap a court to seat the selected player" : "Tap a player below to place them"}
                    </div>
                    <button
                      onClick={() => apply({ auto: true })}
                      disabled={busy}
                      title="Fill courts by sign-up order — stratified by ladder rank when standings are enabled"
                      className="cs-tap px-3 py-1.5 border border-ink/30 hover:border-ink/60 text-[10px] uppercase tracking-wider font-bold disabled:opacity-40 shrink-0"
                    >
                      Auto-fill
                    </button>
                  </div>
                  <p className="text-[11px] text-ink/45 mb-3">
                    Edit each court's number for this week's booking — courts change game to game.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5">
                    {layout.courts.map((c) => (
                      <div
                        key={c.number}
                        onClick={() => onSeatOnCourt(c.number)}
                        className={
                          "text-left border rounded-md px-3 py-2.5 transition " +
                          (selected && !busy
                            ? "border-accent-dim hover:border-ink cursor-pointer"
                            : "border-ink/15")
                        }
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1 min-w-0">
                            <span className="font-display font-bold text-sm shrink-0">Court</span>
                            <input
                              value={labels[c.number - 1] ?? c.label}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                const next = [...labels];
                                next[c.number - 1] = e.target.value;
                                setLabels(next);
                              }}
                              onBlur={() => void saveLabels(labels)}
                              onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
                              aria-label={`Court number for court ${c.number}`}
                              className="w-14 rounded border border-ink/20 px-1.5 py-0.5 font-display font-bold text-sm focus:outline-none focus:border-ink/50 bg-bg"
                            />
                          </div>
                          <div className="font-mono text-[10px] uppercase tracking-wider text-ink/50 shrink-0">
                            {c.players.length}/{perCourt}
                          </div>
                        </div>
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {c.players.map((p) => (
                            <PlayerChip
                              key={p.user_id}
                              person={p}
                              onClick={(e) => {
                                e.stopPropagation();
                                onRemoveFromCourt(p.user_id);
                              }}
                              removable
                            />
                          ))}
                          {c.waiting_for > 0 && (
                            <span className="font-mono text-[10px] text-ink/45 self-center">
                              waiting on {c.waiting_for} more
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {layout.facility_email_set && (
                    <div className="mb-5 pb-5 border-b border-ink/10">
                      <button
                        onClick={() => void sendFacilityRequest()}
                        disabled={busy || layout.locked}
                        title="Finalize now and email the facility — same as the automatic ~24h step. Can't go out more than 24h before the session, and only once."
                        className="cs-tap w-full px-3 py-2 border border-ink/30 hover:border-ink/60 text-[10px] uppercase tracking-wider font-bold disabled:opacity-40"
                      >
                        {layout.locked ? "Court request already sent" : "Send court request to facility"}
                      </button>
                      <p className="text-[11px] text-ink/45 mt-1.5">
                        {layout.locked
                          ? "The roster is locked and the facility request has gone out — it won't be sent again."
                          : "Same as the automatic ~24h step: locks the roster, notifies players, and emails the facility. Won't go out more than 24h before the session."}
                      </p>
                    </div>
                  )}

                  <div className="font-mono text-[10px] uppercase tracking-wider text-ink/55 mb-2">
                    Not on a court yet · {layout.unassigned.length}
                  </div>
                  {layout.unassigned.length === 0 ? (
                    <div className="text-ink/45 text-sm">Everyone confirmed is seated.</div>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {layout.unassigned.map((p) => (
                        <PlayerChip
                          key={p.user_id}
                          person={p}
                          selected={selected === p.user_id}
                          onClick={() => setSelected(selected === p.user_id ? null : p.user_id)}
                        />
                      ))}
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function PlayerChip({
  person,
  selected,
  removable,
  onClick,
}: {
  person: RsvpPersonDTO;
  selected?: boolean;
  removable?: boolean;
  onClick: (e: React.MouseEvent) => void;
}) {
  return (
    <button
      onClick={onClick}
      className={
        "cs-tap inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-display font-bold border " +
        (selected
          ? "bg-accent text-ink border-ink"
          : person.is_you
            ? "bg-ink text-bg border-ink"
            : "bg-bg border-ink/25 hover:border-ink/50")
      }
    >
      {person.name}
      {removable && <span className="text-[10px] opacity-60">×</span>}
    </button>
  );
}
